import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import TimeAgo from "@/components/TimeAgo";
import Paginator from "@/components/items-list/components/Paginator";
import Link from "@/components/Link";
import Tooltip from "@/components/Tooltip";
import ItemsTable, { Columns } from "@/components/items-list/components/ItemsTable";
import LoadingState from "@/components/items-list/components/LoadingState";
import * as Grid from "antd/lib/grid";
import JsonViewInteractive from "@/components/json-view-interactive/JsonViewInteractive";
import QuestionCircleOutlined from "@ant-design/icons/QuestionCircleOutlined";
import ExclamationCircleOutlined from "@ant-design/icons/ExclamationCircleOutlined";
import CheckCircleOutlined from "@ant-design/icons/CheckCircleOutlined";
import { AlertEventTagsControl } from "@/components/tags-control/TagsControl";
import {
  DiscordMessages,
  DiscordMessage,
  DiscordEmbed,
  DiscordEmbedFields,
  DiscordEmbedField
} from '@skyra/discord-components-react';
import { Tweet } from 'react-fake-tweet'
import 'react-fake-tweet/dist/index.css'
import { useTheme } from "@/components/ThemeProvider";
import "./AlertFevents.less";

const columns = [
  Columns.favorites({ className: "p-r-0" }),
  Columns.custom.sortable(
    (text, event) => {
      let parsedProperties;
      let contentText;
  
      try {
        parsedProperties = JSON.parse(event.additional_properties);
        contentText = typeof event.content === 'string' ? event.content.toLowerCase() : JSON.stringify(event.content).toLowerCase();
      } catch (e) {
        parsedProperties = event.additional_properties;
        contentText = event.content;
      }
  
      const statusProperties = parsedProperties?.status?.toLowerCase();
      const statusContent = contentText.toLowerCase().includes('error') ? 'error' : 'ok';
  
      const status =
        statusProperties === "ok" && statusContent === "ok"
          ? "ok"
          : statusProperties === "error" || statusContent === "error"
          ? "error"
          : null;
  
      return (
        <div align="center">
          {status === 'ok' && (
            <Link href={"alert_events/" + event.id}>
              <Tooltip title="OK">
                <CheckCircleOutlined style={{ color: 'green', fontSize: '16px' }} />
              </Tooltip>
            </Link>
          )}
          {status === 'error' && (
            <Link href={"alert_events/" + event.id}>
              <Tooltip title="Error">
                <ExclamationCircleOutlined style={{ color: 'red', fontSize: '16px' }} />
              </Tooltip>
            </Link>
          )}
          {status === null && (
            <Link href={"alert_events/" + event.id}>
              <Tooltip title="Unknown">
                <QuestionCircleOutlined style={{ color: 'orange', fontSize: '16px' }} />
              </Tooltip>
            </Link>
          )}
        </div>
      );
    },
    {
      title: "Status",
      field: "status",
      width: "1%",
      sorter: true
    }
  ),
  
  Columns.custom.sortable(
    (text, event) => (
      <div>
        <Tooltip title={<div align="center">{new Date(event.created_at).toLocaleString()}<br/><TimeAgo date={event.created_at} /></div>}>
          {new Date(event.created_at).toLocaleDateString()}
        </Tooltip>
      </div>
      ),
      {
        title: "Date", 
        field: "created_at", 
        width: "10%" 
      }
  ),
  Columns.custom.sortable(
    (text, event) => (
      <div>
        <React.Fragment>
          <Link className="table-main-title" href={"alert_events/" + event.id}>{event.id}</Link>
          <AlertEventTagsControl className="d-block" tags={event.tags} isArchived={event.is_archived} />
        </React.Fragment>
      </div>
    ),
    {
      title: "ID",
      field: "id",
      width: "1%",
    }
  ),
  Columns.custom.sortable((text, event) => event.alert_type, {
    title: "Alert Type",
    field: "alert_type",
    width: "1%",
  }),
  Columns.custom.sortable(
    (text, alert_event) => (
      <div>
        {alert_event.alert ? (
          <Link href={"alerts/" + alert_event.alert.id+"/overview"}>{alert_event.alert.name}</Link>
        ) : (
          "No alert"
        )}
      </div>
    ),
    {
      title: "Alert Name",
      field: "alert.name",
      width: "1%",
    }
  ),
  Columns.custom.sortable(
    (text, alert_event) => (
      <div>
        {alert_event.query ? (
          <Link href={"queries/" + alert_event.query.id}>{alert_event.query.name}</Link>
        ) : (
          "No query"
        )}
      </div>
    ),
    {
      title: "Query Name",
      field: "query.name",
      width: "1%",
    }
  ),
  Columns.custom.sortable(
    (text, alert_event) => (
      <div>
        {alert_event.user ? (
          <Link href={"users/" + alert_event.user.id}>{alert_event.user.name}</Link>
        ) : (
          "No user"
        )}
      </div>
    ),
    {
      title: "User Name",
      field: "user.name",
      width: "1%",
    }
  ),
  Columns.custom.sortable(
    (text, alert_event) => (
      <div>
        {alert_event.destination ? (
          <Link href={"destinations/" + alert_event.destination.id}>{alert_event.destination.name}</Link>
        ) : (
          "No destination"
        )}
      </div>
    ),
    {
      title: "Destination Name",
      field: "destination.name",
      width: "1%",
    }
  ),
  Columns.centeredWrappableExpandable(
    (text, event) => {
      let parsedContent;
      try {
        parsedContent = JSON.parse(event.content);
      } catch (e) {
        parsedContent = event.content;
      }
      function convertMarkdownLinks(text) {
        const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
        return text.replace(markdownLinkRegex, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
      }

      function decimalColortoHextString(decimalColor) {
        return '#' + decimalColor.toString(16).padStart(6, '0');
      }

      return (
        <div >
          {event.alert_type === "discord_webhook" ? (
            <DiscordMessages>
              <DiscordMessage 
                author={event?.destination?.options?.username} 
                avatar={event?.destination?.options?.avatar_url} 
                timestamp={new Date(event?.created_at).toLocaleDateString()}> 
                <DiscordEmbed
                  slot="embeds"
                  color={parsedContent["embeds"][0]?.color ? decimalColortoHextString(parsedContent["embeds"][0]?.color) : "#0099ff"}
                  embedTitle={parsedContent["embeds"][0]?.title}
                >
                  <div dangerouslySetInnerHTML={{ __html: convertMarkdownLinks(parsedContent["embeds"][0]?.description || '') }} />
                  {parsedContent["embeds"][0]?.fields && (
                    <DiscordEmbedFields slot="fields">
                      {parsedContent["embeds"][0].fields.map((field, index) => (
                        <DiscordEmbedField key={index} fieldTitle={field.name} inline={field.inline}>
                          <div dangerouslySetInnerHTML={{ __html: convertMarkdownLinks(field.value) }} />
                        </DiscordEmbedField>
                      ))}
                    </DiscordEmbedFields>
                  )}
                </DiscordEmbed>
              </DiscordMessage>
            </DiscordMessages>
          ) : event.alert_type === "twitter" ? (
            <Tweet
              config={{
                user: {
                  avatar: event?.destination?.options?.avatar_url,
                  nickname: event?.destination?.options?.account_handle,
                  name: event?.destination?.name,
                },
                text: (
                  <span style={{ fontSize: '14px' , wordBreak: 'break-all' }}>
                    {parsedContent}
                  </span>
                ),
                date: event?.created_at,
              }}
            />
          ):""}
        </div>
      );
    },
    { title: "Content", field: "content", width: "30%" }
  ),
  Columns.centered(
    (text, event) => {
      let parsedProperties;
      try {
        parsedProperties = JSON.parse(event.additional_properties);
      } catch (e) {
        parsedProperties = event.additional_properties;
      }

      return (
        <div className="json-cell">
          {typeof parsedProperties === "object" ? (
            <JsonViewInteractive value={parsedProperties} />
          ) : (
            <pre>{String(parsedProperties)}</pre>
          )}
        </div>
      );
    },
    { title: "Additional Properties", field: "additional_properties", width: "30%" }
  ),
];

function AlertFevents({ events }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [sortedEvents, setSortedEvents] = useState([]);
  const [orderByField, setOrderByField] = useState(null);
  const [orderByReverse, setOrderByReverse] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { isDarkMode } = useTheme();

  useEffect(() => {
    if (events === null) {
      setLoading(true);
    } else if (events.length === 0) {
      setLoading(false);
    } else {
      setSortedEvents(events);
      setLoading(false);
    }
  }, [events]);

  const toggleSorting = (field) => {
    const isReverse = orderByField === field ? !orderByReverse : false;
    setOrderByField(field);
    setOrderByReverse(isReverse);

    const sorted = [...events].sort((a, b) => {
      const getValue = (obj, path) => path.split('.').reduce((o, p) => (o ? o[p] : undefined), obj);

      const aValue = getValue(a, field);
      const bValue = getValue(b, field);

      const isDateField = field === 'created_at' || field === 'date';
      const aValueProcessed = isDateField ? new Date(aValue) : aValue;
      const bValueProcessed = isDateField ? new Date(bValue) : bValue;

      if (aValueProcessed < bValueProcessed) return isReverse ? 1 : -1;
      if (aValueProcessed > bValueProcessed) return isReverse ? -1 : 1;
      return 0;
    });

    setSortedEvents(sorted);
  };

  const filteredEvents = sortedEvents.filter(event =>
    Object.values(event).some(value => {
      // Convert the value to a string if it's not already
      const stringValue = value !== null && value !== undefined ? String(value) : "";
  
      // Check if the stringified value includes the search term
      return stringValue.toLowerCase().includes(searchTerm.toLowerCase());
    })
  );
  const currentItems = filteredEvents.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className={`alert-fevents ${isDarkMode ? 'dark-mode' : ''}`}>
      <h3 className={`alert-fevents-title ${isDarkMode ? 'dark-mode' : ''}`}>Alert History</h3>
      {loading ? (
        <LoadingState />
      ) : (
        <div className={`bg-white tiled p-20 ${isDarkMode ? 'dark-mode' : ''}`}>
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className={`search-input ${isDarkMode ? 'dark-mode' : ''}`}
            style={{ marginBottom: "20px", padding: "10px", width: "100%", borderRadius: "2px" }}
          />
          <Grid.Row gutter={16}>
            <Grid.Col xs={24}>
              <div className={`bg-white tiled table-responsive ${isDarkMode ? 'dark-mode' : ''}`}>
                <ItemsTable
                  items={currentItems}
                  columns={columns}
                  loading={loading}
                  orderByField={orderByField}
                  orderByReverse={orderByReverse}
                  toggleSorting={toggleSorting}
                  sortColumn={orderByField}
                  sortOrder={orderByReverse ? 'descend' : 'ascend'}
                  className={isDarkMode ? 'dark-mode' : ''}
                />
                <Paginator
                  showPageSizeSelect
                  totalCount={filteredEvents.length}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                  page={currentPage}
                  onChange={setCurrentPage}
                  className={isDarkMode ? 'dark-mode' : ''}
                />
              </div>
            </Grid.Col>
          </Grid.Row>
        </div>
      )}
    </div>
  );
}

AlertFevents.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      action: PropTypes.string.isRequired,
      user: PropTypes.oneOfType([PropTypes.object, PropTypes.string]), 
      alert_type: PropTypes.string.isRequired,
      alert: PropTypes.shape({ 
        name: PropTypes.string,
      }),
      query: PropTypes.shape({ 
        name: PropTypes.string,
      }),
      destination: PropTypes.shape({ 
        name: PropTypes.string,
      }),
      alert_id: PropTypes.string.isRequired,
      query_id: PropTypes.string.isRequired,
      destination_id: PropTypes.string.isRequired,
      additional_properties: PropTypes.object.isRequired,
      content: PropTypes.object.isRequired,
      favorite: PropTypes.bool,
    })
  ).isRequired,
};

export default AlertFevents;
